import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '../api/client'
import { externalProviderQuery, type ThirdPartyEmailSpec, type ThirdPartyTemplate } from '../api/easeedesk'
import { saveTemplateForProvider } from '../api/settingsEaseedeskEmailTemplates'
import { toast } from '../ui/notify'
import { renderSubject, renderTemplate } from './thirdPartyEmail'

/**
 * EMAILUL CATRE FURNIZOR, in formularul "Waiting for Third Party" - in locul
 * campului liber "Custom details".
 *
 * Se alege un sablon (Settings > Easeedesk > Ticket notifications > Templates
 * > Third party escalation templates; implicitul vine ales). Subiectul si
 * mesajul apar GATA UMPLUTE cu datele tichetului si ale furnizorului ales, si
 * se pot schimba ca text obisnuit: ce se vede in casute e exact ce pleaca.
 * De-aia nu mai e nici previzualizare, nici lista de variabile (cerinta
 * Roxanei, 6 octombrie 2026, tichetul 236).
 *
 * DESTINATARUL nu se scrie aici: il pune serverul (`ThirdPartyEscalation::
 * recipient`) - contactul de escaladare al aplicatiei, cand furnizorul e al
 * unei aplicatii de pe tichet, altfel adresa din fisa furnizorului. Formularul
 * doar o arata. Un furnizor fara adresa: se spune, cu legatura spre locul unde
 * se pune, si escaladarea pleaca fara email.
 *
 * SABLONUL PE FURNIZOR (cerinta Roxanei, 7 octombrie 2026: "un furnizor cere
 * niste date, altul altele"):
 *
 *  - furnizorul ales are sablonul lui (`spec.byProvider`): vine ales SINGUR,
 *    si la deschidere, si cand se alege alt furnizor - cat timp omul n-a scris
 *    nimic de mana. Altfel, implicitul general (afara de un sablon ales de
 *    mana din lista, care ramane). Sablonul furnizorului sta primul in lista,
 *    "(for <furnizor>)".
 *  - "Edit template text" arata sablonul BRUT, cu variabile (`{$ticket.title}`):
 *    ce se scrie acolo umple din nou mesajul de deasupra, iar de acolo se
 *    salveaza - "Save as template for <furnizor>" (sablon nou, legat de el)
 *    sau "Update template for <furnizor>" (cu confirmare). In sablon ajung
 *    VARIABILELE, niciodata datele acestui tichet. Ce se scrie direct in mesaj
 *    ramane numai pentru emailul asta.
 *  - butonul cere dreptul sabloanelor din Settings (`spec.canSaveTemplate`);
 *    fara el, nu apare.
 *
 * Campul formularului (`third_party_email`) tine, ca JSON: `{subject, body}`
 * cand emailul e gata, `{noEmail: true}` cand furnizorul n-are adresa, sau gol
 * cand lipseste ceva - de-aia "required" il prinde ca pe orice camp.
 */
export function ThirdPartyEmailField({
  id,
  spec,
  providerId,
  providerName,
  onChange,
}: {
  id: number
  spec: ThirdPartyEmailSpec
  providerId: number
  providerName: string
  onChange(next: string): void
}) {
  /* Locale: un sablon salvat de aici intra in lista fara reincarcare. */
  const [templates, setTemplates] = useState<ThirdPartyTemplate[]>(spec.templates)
  const [byProvider, setByProvider] = useState<Record<string, number>>(spec.byProvider ?? {})

  /** Sablonul furnizorului, altfel implicitul general, altfel primul. */
  const templateFor = (provider: number): ThirdPartyTemplate | undefined => {
    const own = provider > 0 ? byProvider[String(provider)] : undefined
    return (own !== undefined ? templates.find((t) => t.id === own) : undefined) ?? templates.find((t) => t.isDefault) ?? templates[0]
  }

  const [first] = useState(() => templateFor(providerId))
  const [templateId, setTemplateId] = useState<number>(first?.id ?? 0)
  /** Sablonul brut, cu variabile: din el se umple mesajul si din el se salveaza. */
  const [raw, setRaw] = useState({ subject: first?.subject ?? '', body: first?.body ?? '' })
  const [touched, setTouched] = useState(false)
  const [rawEdited, setRawEdited] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(false)
  /** Sablonul ales DE MANA din lista: alt furnizor fara sablon propriu nu-l schimba. */
  const [manual, setManual] = useState(false)

  const vendor = providerId > 0 ? spec.vendors[String(providerId)] : undefined
  const known = providerId > 0 && spec.recipients !== undefined && String(providerId) in spec.recipients
  /* Un furnizor adaugat din formular dupa deschidere nu e in `recipients`: fisa lui. */
  const provider = useQuery({ ...externalProviderQuery(providerId), enabled: providerId > 0 && !known })
  const recipient = providerId < 1 ? '' : known ? (spec.recipients?.[String(providerId)] ?? '') : (provider.data?.email ?? '').trim()
  const looking = providerId > 0 && !known && provider.isPending
  const name = providerName || provider.data?.name || ''

  const values = useMemo(
    () => ({ ...spec.values, '{$provider.name}': name, ...(vendor?.values ?? {}) }),
    [spec.values, name, vendor],
  )

  const fill = (t: { subject: string; body: string }, v: Record<string, string>) => ({
    subject: renderSubject(t.subject, v),
    body: renderTemplate(t.body, v),
  })
  const [text, setText] = useState(() => fill(raw, values))

  /* Alt furnizor: sablonul LUI, cat timp omul n-a scris nimic de mana. */
  const lastProvider = useRef(providerId)
  useEffect(() => {
    if (lastProvider.current === providerId) return
    lastProvider.current = providerId
    if (touched || rawEdited) return
    const hasOwn = providerId > 0 && byProvider[String(providerId)] !== undefined
    if (manual && !hasOwn) return
    const next = templateFor(providerId)
    if (next && next.id !== templateId) {
      setTemplateId(next.id)
      setManual(false)
      setRaw({ subject: next.subject, body: next.body })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId])

  /* Alte valori sau alt sablon brut: textul se umple din nou, cat timp omul nu
     l-a schimbat de mana. Schimbat, ramane al lui. */
  const filledFor = useRef({ values, raw })
  useEffect(() => {
    if (filledFor.current.values === values && filledFor.current.raw === raw) return
    filledFor.current = { values, raw }
    if (!touched) setText(fill(raw, values))
  }, [values, raw, touched])

  const noEmail = providerId > 0 && !looking && recipient === ''
  const subject = text.subject.trim()
  const body = text.body.trim()
  const payload =
    providerId < 1 || looking ? '' : noEmail ? JSON.stringify({ noEmail: true }) : subject !== '' && body !== '' ? JSON.stringify({ subject, body }) : ''

  /* `onChange` vine nou la fiecare desenare a formularului; pe el nu se
     reia nimic, numai pe ce s-a schimbat in email. */
  const report = useRef(onChange)
  useEffect(() => {
    report.current = onChange
  })
  useEffect(() => {
    report.current(payload)
  }, [payload])

  const choose = (next: number) => {
    const picked = templates.find((t) => t.id === next)
    if (!picked) return
    setTemplateId(next)
    setManual(true)
    setTouched(false)
    setRawEdited(false)
    setRaw({ subject: picked.subject, body: picked.body })
  }

  const ownId = providerId > 0 ? byProvider[String(providerId)] : undefined
  const own = ownId !== undefined ? templates.find((t) => t.id === ownId) : undefined
  /* Sablonul furnizorului sus, apoi restul in ordinea serverului (implicitul intai). */
  const ordered = own ? [own, ...templates.filter((t) => t.id !== own.id)] : templates

  const save = useMutation({
    mutationFn: () => saveTemplateForProvider(providerId, raw.subject, raw.body),
    onSuccess: (answer) => {
      const saved = answer.template
      if (!saved) return
      setTemplates((list) =>
        list.some((t) => t.id === saved.id)
          ? list.map((t) => (t.id === saved.id ? { ...t, subject: saved.subject, body: saved.body, providers: saved.providers } : t))
          : [...list, saved],
      )
      setByProvider((links) => ({ ...links, [String(providerId)]: saved.id }))
      setTemplateId(saved.id)
      setRaw({ subject: saved.subject, body: saved.body })
      setRawEdited(false)
      toast(answer.created ? `Template "${saved.name}" created for ${name}.` : `Template "${saved.name}" updated.`, 'Success')
    },
    onError: (error) => toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  const saveAsTemplate = () => {
    if (raw.subject.trim() === '' || raw.body.trim() === '') return
    if (own) {
      const others = (own.providers ?? []).filter((p) => p !== providerId).length
      const shared = others > 0 ? ` It is also used for ${others} other provider${others === 1 ? '' : 's'}.` : ''
      if (!window.confirm(`Overwrite the template "${own.name}" used for ${name} with this text?${shared}`)) return
    }
    save.mutate()
  }

  const editRaw = (next: { subject: string; body: string }) => {
    setRawEdited(true)
    /* Sablonul scris aici umple din nou mesajul: ce se vede deasupra e ce pleaca. */
    setTouched(false)
    setRaw(next)
  }

  const name0 = `tp-${id}`
  const canSave = spec.canSaveTemplate === true && providerId > 0

  if (noEmail) {
    return (
      <div className="tk-third-party-email">
        <div className="form-group">
          <span></span>
          <div className="alert alert-warning tk-third-party-no-email" role="alert">
            <i className="fas fa-envelope" aria-hidden="true"></i> <strong>{name || 'This provider'}</strong> has no email address - add it in{' '}
            <a href="/app/settings/easeedesk/external_service_providers" target="_blank" rel="noopener noreferrer">
              Settings &gt; Easeedesk &gt; External service providers
            </a>
            {vendor && (
              <>
                {' '}
                or as an escalation contact on{' '}
                <a href={`/app/applications/${vendor.appId}`} target="_blank" rel="noopener noreferrer">
                  {vendor.app}
                </a>
              </>
            )}
            . No email will be sent; the ticket still waits for the third party.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="tk-third-party-email">
      <div className="form-group">
        <label htmlFor={`${name0}-template`} className="control-label">
          Email template
        </label>
        <select id={`${name0}-template`} className="form-control input-sm" value={templateId} onChange={(ev) => choose(Number(ev.target.value))}>
          {ordered.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
              {own && t.id === own.id ? ` (for ${name})` : t.isDefault && templates.length > 1 ? ' (default)' : ''}
            </option>
          ))}
        </select>
      </div>

      {vendor && (
        <div className="form-group">
          <span></span>
          <div className="alert alert-warning tk-vendor-case-note" role="note">
            <i className="fas fa-headset" aria-hidden="true"></i> {name} supports <strong>{vendor.app}</strong>: sending also opens a
            vendor case (response deadline, escalation levels, overdue alerts).
          </div>
        </div>
      )}

      <div className="form-group">
        <label htmlFor={`${name0}-subject`} className="control-label">
          Subject <span className="required">*</span>
        </label>
        <input
          id={`${name0}-subject`}
          type="text"
          className="form-control input-sm"
          value={text.subject}
          onChange={(ev) => {
            setTouched(true)
            setText({ ...text, subject: ev.target.value })
          }}
        />
      </div>

      <div className="form-group">
        <label htmlFor={`${name0}-body`} className="control-label">
          Message <span className="required">*</span>
        </label>
        <div>
          <textarea
            id={`${name0}-body`}
            className="form-control"
            rows={9}
            value={text.body}
            onChange={(ev) => {
              setTouched(true)
              setText({ ...text, body: ev.target.value })
            }}
          />
          <p className="help-block tk-third-party-to">
            {providerId < 1
              ? 'Choose the provider first: the email goes to its address on file.'
              : looking
                ? 'Looking up the address of the provider...'
                : `Sent to ${recipient}, from the ticket conversation: the reply comes back into this ticket.`}
          </p>
          {canSave && (
            <p className="tk-third-party-template-toggle">
              <a
                href="#"
                className="color-blue tp-edit-template"
                aria-expanded={editingTemplate}
                onClick={(ev) => {
                  ev.preventDefault()
                  setEditingTemplate(!editingTemplate)
                }}
              >
                <i className={`fas ${editingTemplate ? 'fa-chevron-up' : 'fa-code'}`} aria-hidden="true"></i>{' '}
                {editingTemplate ? 'Hide template text' : 'Edit template text'}
              </a>
            </p>
          )}
        </div>
      </div>

      {canSave && editingTemplate && (
        <div className="form-group tp-template-editor">
          <span className="control-label">Template text</span>
          <div className="well well-sm">
            <p className="help-block" style={{ marginTop: 0 }}>
              The template with its variables, as it is saved for {name || 'the provider'}. Editing it fills the message above again
              {touched ? ' (your direct changes to the message are replaced)' : ''}; nothing from this ticket goes into the template.
            </p>
            <label htmlFor={`${name0}-raw-subject`} className="control-label">
              Template subject
            </label>
            <input
              id={`${name0}-raw-subject`}
              type="text"
              className="form-control input-sm"
              value={raw.subject}
              onChange={(ev) => editRaw({ ...raw, subject: ev.target.value })}
            />
            <label htmlFor={`${name0}-raw-body`} className="control-label" style={{ marginTop: 8 }}>
              Template body
            </label>
            <textarea
              id={`${name0}-raw-body`}
              className="form-control"
              rows={9}
              value={raw.body}
              onChange={(ev) => editRaw({ ...raw, body: ev.target.value })}
            />
            <p className="help-block">
              Variables:{' '}
              {spec.variables.map((v) => (
                <code key={v.token} title={v.label} style={{ display: 'inline-block', margin: '0 4px 2px 0' }}>
                  {v.token}
                </code>
              ))}
            </p>
            <button
              type="button"
              className="btn btn-xs btn-primary tp-save-template"
              disabled={save.isPending || raw.subject.trim() === '' || raw.body.trim() === ''}
              onClick={saveAsTemplate}
            >
              <i className="fas fa-save" aria-hidden="true"></i> {own ? `Update template for ${name}` : `Save as template for ${name}`}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
